/*
 * "Bring your own curves" (sections/easeflow-curves.liquid).
 *
 * A short tour, while the section is on screen: a curve is copied from
 * the "web" card, pasted into the field under the graph (the graph morphs
 * to it, the dot runs the motion strip), saved with + into a "My Curves"
 * library and that library exported as My Curves.json. Any touch hands
 * the demo over: the visitor pastes or types a curve (four numbers, a
 * cubic-bezier() or a cubic-bezier.com link, as EaseFlow's field takes),
 * drags the handles, saves and exports. The real clipboard is never read:
 * a normal paste into the field just works, and a copy while a snippet is
 * focused or hovered puts that snippet on the clipboard.
 */
(() => {
  const SVG = 'http://www.w3.org/2000/svg';
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const apple = /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');

  // ---- curves -----------------------------------------------------------
  const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
  const mix = (a, b, t) => a + (b - a) * t;
  const Y_MIN = -1;
  const Y_MAX = 2;

  const at = (t, p1, p2) => {
    const m = 1 - t;
    return 3 * m * m * t * p1 + 3 * m * t * t * p2 + t * t * t;
  };

  const ease = (c, x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 32; i += 1) {
      const mid = (lo + hi) / 2;
      if (at(mid, c.x1, c.x2) < x) lo = mid;
      else hi = mid;
    }
    return at((lo + hi) / 2, c.y1, c.y2);
  };

  const make = (v) => ({ x1: clamp(v[0], 0, 1), y1: clamp(v[1], Y_MIN, Y_MAX), x2: clamp(v[2], 0, 1), y2: clamp(v[3], Y_MIN, Y_MAX) });
  const same = (a, b) => !!a && !!b && ['x1', 'y1', 'x2', 'y2'].every((k) => Math.abs(a[k] - b[k]) < 1e-4);

  const KEYWORDS = {
    linear: [0, 0, 1, 1],
    ease: [0.25, 0.1, 0.25, 1],
    'ease-in': [0.42, 0, 1, 1],
    'ease-out': [0, 0, 0.58, 1],
    'ease-in-out': [0.42, 0, 0.58, 1],
  };

  // What the field takes (EaseFlow's field): four numbers, cubic-bezier(),
  // a cubic-bezier.com link, or a CSS keyword.
  const parse = (text) => {
    let source = String(text || '').replace(/[\u2010-\u2015\u2212]/g, '-').trim().toLowerCase();
    if (!source) return null;
    const keyword = source.replace(/^.*timing-function\s*:\s*/, '').replace(/[;\s]+$/, '');
    if (KEYWORDS[keyword]) return make(KEYWORDS[keyword]);
    const hash = source.indexOf('#');
    if (hash !== -1 && source.indexOf('cubic-bezier.com') !== -1) source = source.slice(hash + 1);
    else if (source.indexOf('cubic-bezier') !== -1) source = source.slice(source.indexOf('cubic-bezier'));
    const found = source.match(/[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/g);
    if (!found || found.length !== 4) return null;
    const v = found.map(Number);
    if (!v.every(Number.isFinite) || v[0] < 0 || v[0] > 1 || v[2] < 0 || v[2] > 1) return null;
    return make(v);
  };

  const num = (v) => {
    const r = Math.round(v * 1000) / 1000;
    return r === 0 ? '0' : String(r);
  };
  const format = (c) => [c.x1, c.y1, c.x2, c.y2].map(num).join(', ');

  const extent = (c) => {
    let lo = Math.min(0, c.y1, c.y2);
    let hi = Math.max(1, c.y1, c.y2);
    for (let i = 1; i < 48; i += 1) {
      const y = at(i / 48, c.y1, c.y2);
      lo = Math.min(lo, y);
      hi = Math.max(hi, y);
    }
    return { min: lo < 0 ? lo - 0.04 : 0, max: hi > 1 ? hi + 0.04 : 1 };
  };

  const reach = (c) => {
    let lo = 0;
    let hi = 1;
    for (let i = 1; i < 48; i += 1) {
      const y = ease(c, i / 48);
      lo = Math.min(lo, y);
      hi = Math.max(hi, y);
    }
    return { min: lo, max: hi };
  };

  // Default library tiles (EaseFlow's Default pack)
  const DEFAULTS = [
    ['Sine In Out', [0.37, 0, 0.63, 1]],
    ['Cubic Out', [0.33, 1, 0.68, 1]],
    ['Expo In Out', [0.87, 0, 0.13, 1]],
    ['Back Out', [0.34, 1.56, 0.64, 1]],
  ].map(([name, v]) => ({ name, curve: make(v) }));

  const node = (name, attrs, parent) => {
    const el = document.createElementNS(SVG, name);
    Object.keys(attrs || {}).forEach((k) => el.setAttribute(k, attrs[k]));
    if (parent) parent.appendChild(el);
    return el;
  };

  const thumb = (c) => {
    const w = 60;
    const h = 40;
    const r = extent(c);
    const y = (v) => 6 + (1 - (v - r.min) / (r.max - r.min)) * (h - 12);
    let d = '';
    for (let i = 0; i <= 30; i += 1) d += `${i ? 'L' : 'M'}${(6 + (i / 30) * (w - 12)).toFixed(1)} ${y(ease(c, i / 30)).toFixed(1)}`;
    return `<svg viewBox="0 0 ${w} ${h}" aria-hidden="true"><path d="${d}"/></svg>`;
  };

  const setup = (root) => {
    if (root.dataset.efcReady) return;
    root.dataset.efcReady = '1';
    const $ = (n) => root.querySelector(`[data-efc="${n}"]`);
    const $$ = (n) => Array.from(root.querySelectorAll(`[data-efc="${n}"]`));
    const stage = $('stage');
    const svg = $('svg');
    const field = $('field');
    if (!stage || !svg || !field) return;

    const copy = {};
    const tpl = $('copy');
    if (tpl) Array.from(tpl.content.querySelectorAll('[data-k]')).forEach((el) => (copy[el.dataset.k] = el.textContent.trim()));

    const snips = $$('snip');
    const status = $('status');
    const caption = $('caption');
    const dots = Array.from(root.querySelectorAll('.efc-dots li'));
    const saveBtn = $('save');
    const moreBtn = $('more');
    const menu = $('menu');
    const exportItem = $('export');
    const importItem = $('import');
    const sheet = $('sheet');
    const nameInput = $('name');
    const sheetCurve = $('sheet-curve');
    const confirmBtn = $('confirm');
    const cancelBtn = $('cancel');
    const grid = $('grid');
    const tabs = $$('tab');
    const mineTab = tabs.find((t) => t.dataset.lib === 'mine');
    const file = $('file');
    const fileName = $('file-name');
    const fileCount = $('file-count');
    const copyHud = $('copy-hud');
    const pasteHud = $('paste-hud');
    const replay = $('replay');
    const myLibrary = copy.my_library || 'My Curves';
    // [name]-style tokens in a translated text, filled in
    const fill = (text, values) => text.replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));

    $$('mod').forEach((cap) => (cap.textContent = apple ? '⌘' : 'Ctrl'));
    if (exportItem) exportItem.textContent = `${copy.export || 'Export'} “${myLibrary}”…`;

    // ---- state ----------------------------------------------------------
    const START = DEFAULTS[0].curve;
    const state = { curve: START, shown: START, range: extent(START), lib: 'default', mine: [], visible: false, yours: false };

    // ---- the graph --------------------------------------------------------
    const PAD = 16;
    const box = { w: 0, h: 0 };
    const gridLayer = node('g', {}, svg);
    const square = node('rect', { class: 'efc-g-square', rx: 3 }, svg);
    const lineOne = node('line', { class: 'efc-g-line' }, svg);
    const lineTwo = node('line', { class: 'efc-g-line' }, svg);
    const glow = node('path', { class: 'efc-g-glow' }, svg);
    const path = node('path', { class: 'efc-g-curve' }, svg);
    const endA = node('circle', { class: 'efc-g-end', r: 3.5 }, svg);
    const endB = node('circle', { class: 'efc-g-end', r: 3.5 }, svg);
    const hitOne = node('circle', { class: 'efc-g-hit', r: 18 }, svg);
    const hitTwo = node('circle', { class: 'efc-g-hit', r: 18 }, svg);
    const knobOne = node('circle', { class: 'efc-g-knob', r: 6.5 }, svg);
    const knobTwo = node('circle', { class: 'efc-g-knob', r: 6.5 }, svg);
    let activeKnob = 'p1';

    const sx = (x) => PAD + x * (box.w - 2 * PAD);
    const sy = (y) => PAD + (1 - (y - state.range.min) / (state.range.max - state.range.min)) * (box.h - 2 * PAD);
    let gridKey = '';

    const draw = () => {
      if (!box.w) return;
      const c = state.shown;
      const key = `${box.w}|${box.h}|${state.range.min.toFixed(3)}|${state.range.max.toFixed(3)}`;
      if (key !== gridKey) {
        gridKey = key;
        gridLayer.textContent = '';
        for (let i = 1; i < 4; i += 1) {
          node('line', { class: 'efc-g-grid', x1: sx(i / 4), x2: sx(i / 4), y1: sy(state.range.max), y2: sy(state.range.min) }, gridLayer);
          node('line', { class: 'efc-g-grid', x1: sx(0), x2: sx(1), y1: sy(i / 4), y2: sy(i / 4) }, gridLayer);
        }
        [0, 1].forEach((level) => node('line', { class: 'efc-g-limit', x1: 4, x2: box.w - 4, y1: sy(level), y2: sy(level) }, gridLayer));
      }
      square.setAttribute('x', sx(0));
      square.setAttribute('y', sy(1));
      square.setAttribute('width', Math.max(0, sx(1) - sx(0)));
      square.setAttribute('height', Math.max(0, sy(0) - sy(1)));
      const count = Math.max(48, Math.round((box.w - 2 * PAD) / 3));
      let d = `M${sx(0).toFixed(1)},${sy(0).toFixed(1)}`;
      for (let i = 1; i <= count; i += 1) d += `L${sx(i / count).toFixed(1)},${sy(ease(c, i / count)).toFixed(1)}`;
      path.setAttribute('d', d);
      glow.setAttribute('d', d);
      const line = (el, ax, ay, bx, by) => {
        el.setAttribute('x1', sx(ax).toFixed(1));
        el.setAttribute('y1', sy(ay).toFixed(1));
        el.setAttribute('x2', sx(bx).toFixed(1));
        el.setAttribute('y2', sy(by).toFixed(1));
      };
      const place = (el, x, y) => {
        el.setAttribute('cx', sx(x).toFixed(1));
        el.setAttribute('cy', sy(y).toFixed(1));
      };
      line(lineOne, 0, 0, c.x1, c.y1);
      line(lineTwo, 1, 1, c.x2, c.y2);
      place(endA, 0, 0);
      place(endB, 1, 1);
      place(knobOne, c.x1, c.y1);
      place(hitOne, c.x1, c.y1);
      place(knobTwo, c.x2, c.y2);
      place(hitTwo, c.x2, c.y2);
      knobOne.classList.toggle('is-active', activeKnob === 'p1');
      knobTwo.classList.toggle('is-active', activeKnob === 'p2');
    };

    const measure = () => {
      const r = svg.parentElement.getBoundingClientRect();
      box.w = Math.max(80, Math.round(r.width));
      box.h = Math.max(80, Math.round(r.height));
      svg.setAttribute('viewBox', `0 0 ${box.w} ${box.h}`);
      gridKey = '';
      draw();
      strip.measure();
    };

    let morph = null;
    const show = (c, animate) => {
      if (morph) cancelAnimationFrame(morph);
      morph = null;
      state.curve = c;
      const toRange = extent(c);
      if (!animate || still.matches || !box.w) {
        state.shown = c;
        state.range = toRange;
        draw();
        return;
      }
      const from = Object.assign({}, state.shown);
      const fromRange = Object.assign({}, state.range);
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / 320);
        const e = 1 - Math.pow(1 - p, 3);
        state.shown = { x1: mix(from.x1, c.x1, e), y1: mix(from.y1, c.y1, e), x2: mix(from.x2, c.x2, e), y2: mix(from.y2, c.y2, e) };
        state.range = { min: mix(fromRange.min, toRange.min, e), max: mix(fromRange.max, toRange.max, e) };
        draw();
        morph = p < 1 ? requestAnimationFrame(step) : null;
      };
      morph = requestAnimationFrame(step);
    };

    // ---- the motion strip ---------------------------------------------------
    const strip = (() => {
      const host = $('motion');
      const track = host.querySelector('.efc-m-track');
      const marks = host.querySelectorAll('.efc-m-mark');
      const dot = host.querySelector('.efc-m-dot');
      const INSET = 18;
      const LEAD = 160;
      const MOVE = 1000;
      const REST = 420;
      const CYCLE = LEAD + MOVE + REST;
      let width = 0;
      let raf = null;
      let start = 0;
      let until = 0;
      let t = 1;
      let opacity = 1;
      const paint = () => {
        if (!width) return;
        const r = reach(state.curve);
        const scale = Math.max(1, width - 2 * INSET) / (r.max - r.min);
        const px = (v) => INSET + (v - r.min) * scale;
        track.style.transform = `translate3d(${px(0).toFixed(1)}px,0,0)`;
        track.style.width = `${(px(1) - px(0)).toFixed(1)}px`;
        marks[0].style.transform = `translate3d(${px(0).toFixed(1)}px,0,0)`;
        marks[1].style.transform = `translate3d(${px(1).toFixed(1)}px,0,0)`;
        dot.style.transform = `translate3d(${px(ease(state.curve, t)).toFixed(1)}px,0,0)`;
        dot.style.opacity = opacity.toFixed(3);
      };
      const tick = (now) => {
        raf = null;
        const e = (now - start) % CYCLE;
        t = clamp((e - LEAD) / MOVE, 0, 1);
        opacity = e < 120 ? e / 120 : e > CYCLE - 160 ? (CYCLE - e) / 160 : 1;
        if (now >= until) {
          t = 1;
          opacity = 1;
          paint();
          return;
        }
        paint();
        if (state.visible && !document.hidden) raf = requestAnimationFrame(tick);
      };
      return {
        measure() {
          width = host.clientWidth;
          paint();
        },
        play(times = 2) {
          if (still.matches) {
            t = 1;
            paint();
            return;
          }
          start = performance.now();
          until = start + times * CYCLE - REST;
          if (raf === null && state.visible) raf = requestAnimationFrame(tick);
        },
        resume() {
          if (raf === null && performance.now() < until && state.visible) raf = requestAnimationFrame(tick);
        },
        halt() {
          if (raf !== null) cancelAnimationFrame(raf);
          raf = null;
        },
        paint,
        host,
      };
    })();
    strip.host.addEventListener('click', () => strip.play(1));

    // ---- the field ------------------------------------------------------------
    const say = (text, kind) => {
      status.textContent = text || ' ';
      status.classList.toggle('is-ok', kind === 'ok');
      status.classList.toggle('is-bad', kind === 'bad');
    };

    const markCurrent = () => {
      Array.from(grid.children).forEach((tile) => tile.classList.toggle('is-current', same(tile._curve, state.curve)));
    };

    const load = (c, opts = {}) => {
      const changed = !same(c, state.curve);
      show(c, opts.animate !== false);
      if (opts.field !== false) field.value = format(c);
      markCurrent();
      if (changed || opts.play) strip.play(opts.times || 2);
    };

    const commitField = () => {
      const text = field.value;
      if (!text.trim()) {
        field.classList.remove('is-invalid');
        say('');
        return;
      }
      const c = parse(text);
      if (!c) {
        field.classList.add('is-invalid');
        say(copy.invalid, 'bad');
        return;
      }
      field.classList.remove('is-invalid');
      say(`cubic-bezier(${format(c)})`, 'ok');
      load(c);
    };

    let typing = null;
    field.addEventListener('input', () => {
      clearTimeout(typing);
      const c = parse(field.value);
      if (c) {
        field.classList.remove('is-invalid');
        say(`cubic-bezier(${format(c)})`, 'ok');
        load(c, { field: false });
      } else {
        typing = setTimeout(() => {
          if (field.value.trim() && !parse(field.value)) {
            field.classList.add('is-invalid');
            say(copy.invalid, 'bad');
          }
        }, 700);
      }
    });
    field.addEventListener('paste', () => setTimeout(commitField, 0));
    field.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitField();
        field.select();
      } else if (event.key === 'Escape') {
        field.value = format(state.curve);
        field.classList.remove('is-invalid');
        say('');
      }
    });
    field.addEventListener('blur', () => {
      if (parse(field.value)) field.value = format(state.curve);
    });

    // ---- dragging the handles ---------------------------------------------------
    let drag = null;
    const toValue = (event) => {
      const r = svg.getBoundingClientRect();
      const x = (event.clientX - r.left - PAD) / (box.w - 2 * PAD);
      const y = state.range.min + (1 - (event.clientY - r.top - PAD) / (box.h - 2 * PAD)) * (state.range.max - state.range.min);
      return { x: clamp(x, 0, 1), y: clamp(y, Y_MIN, Y_MAX) };
    };
    const moveHandle = (handle, p) => {
      const c = Object.assign({}, state.curve);
      if (handle === 'p1') {
        c.x1 = Math.round(p.x * 100) / 100;
        c.y1 = Math.round(p.y * 100) / 100;
      } else {
        c.x2 = Math.round(p.x * 100) / 100;
        c.y2 = Math.round(p.y * 100) / 100;
      }
      state.curve = c;
      state.shown = c;
      field.value = format(c);
      field.classList.remove('is-invalid');
      say(`cubic-bezier(${format(c)})`, 'ok');
      draw();
      strip.paint();
      markCurrent();
    };
    svg.addEventListener('pointerdown', (event) => {
      const r = svg.getBoundingClientRect();
      const px = event.clientX - r.left;
      const py = event.clientY - r.top;
      const dist = (x, y) => Math.hypot(sx(x) - px, sy(y) - py);
      const one = dist(state.curve.x1, state.curve.y1);
      const two = dist(state.curve.x2, state.curve.y2);
      const handle = one <= two ? 'p1' : 'p2';
      if (Math.min(one, two) > 26) return;
      event.preventDefault();
      activeKnob = handle;
      drag = { handle, id: event.pointerId };
      try {
        svg.setPointerCapture(event.pointerId);
      } catch (e) {
        /* not capturable */
      }
      if (morph) {
        cancelAnimationFrame(morph);
        morph = null;
        state.shown = state.curve;
      }
    });
    svg.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      moveHandle(drag.handle, toValue(event));
    });
    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      drag = null;
      state.range = extent(state.curve);
      gridKey = '';
      draw();
      strip.play(1);
    };
    svg.addEventListener('pointerup', endDrag);
    svg.addEventListener('pointercancel', endDrag);
    svg.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 0.1 : 0.01;
      const dirs = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
      if (event.key === 'Tab' && !event.shiftKey && activeKnob === 'p1') {
        event.preventDefault();
        activeKnob = 'p2';
        draw();
        return;
      }
      if (!dirs[event.key]) return;
      event.preventDefault();
      const c = state.curve;
      const p = activeKnob === 'p1' ? { x: c.x1, y: c.y1 } : { x: c.x2, y: c.y2 };
      moveHandle(activeKnob, { x: clamp(p.x + dirs[event.key][0], 0, 1), y: clamp(p.y + dirs[event.key][1], Y_MIN, Y_MAX) });
      state.range = extent(state.curve);
      gridKey = '';
      draw();
    });

    // ---- the library -------------------------------------------------------------
    const renderGrid = (fresh) => {
      grid.textContent = '';
      const list = state.lib === 'mine' ? state.mine : DEFAULTS;
      list.forEach((preset) => {
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'efc-preset';
        tile._curve = preset.curve;
        tile.innerHTML = `<span class="efc-thumb">${thumb(preset.curve)}</span><span class="efc-preset-name"></span>`;
        tile.lastChild.textContent = preset.name;
        if (preset === fresh) tile.classList.add('is-new');
        tile.addEventListener('click', () => {
          load(preset.curve, { play: true });
          say(preset.name, 'ok');
        });
        grid.appendChild(tile);
      });
      markCurrent();
    };

    const selectLib = (lib) => {
      state.lib = lib;
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.lib === lib)));
      renderGrid();
    };
    tabs.forEach((t) => t.addEventListener('click', () => selectLib(t.dataset.lib)));

    const nextName = () => {
      const base = copy.curve_name || 'My Curve';
      if (!state.mine.some((p) => p.name === base)) return base;
      let n = 2;
      while (state.mine.some((p) => p.name === `${base} ${n}`)) n += 1;
      return `${base} ${n}`;
    };

    const addPreset = (name) => {
      const preset = { name: name || nextName(), curve: Object.assign({}, state.curve) };
      if (state.mine.length >= 8) state.mine.shift();
      state.mine.push(preset);
      const firstTime = mineTab.hidden;
      mineTab.hidden = false;
      if (firstTime) {
        mineTab.classList.remove('is-new');
        void mineTab.offsetWidth;
        mineTab.classList.add('is-new');
      }
      state.lib = 'mine';
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.lib === 'mine')));
      renderGrid(preset);
      showFile(false);
      say(fill(copy.saved_to || 'Saved “[name]” to [library]', { name: preset.name, library: myLibrary }), 'ok');
    };

    const openSheet = () => {
      sheetCurve.textContent = `cubic-bezier(${format(state.curve)})`;
      nameInput.value = '';
      sheet.hidden = false;
    };
    const closeSheet = () => {
      sheet.hidden = true;
    };
    const fileCountText = () => (state.mine.length === 1 ? copy.curves_one || '1 curve' : `${state.mine.length} ${copy.curves_many || 'curves'}`);
    const showFile = (on) => {
      fileCount.textContent = fileCountText();
      fileName.textContent = `${myLibrary}.json`;
      file.setAttribute('aria-hidden', String(!on));
      if (on) {
        file.classList.remove('is-on');
        void file.offsetWidth;
      }
      file.classList.toggle('is-on', !!on);
    };
    const openMenu = (on) => {
      menu.hidden = !on;
      moreBtn.setAttribute('aria-expanded', String(!!on));
      exportItem.disabled = !state.mine.length;
      exportItem.style.opacity = state.mine.length ? '' : '0.45';
    };
    const exportNow = () => {
      openMenu(false);
      if (!state.mine.length) return;
      showFile(true);
      say(fill(copy.exported || 'Exported “[library]”', { library: myLibrary }), 'ok');
    };

    saveBtn.addEventListener('click', () => {
      openMenu(false);
      openSheet();
      nameInput.value = nextName();
      nameInput.focus();
      nameInput.select();
    });
    confirmBtn.addEventListener('click', () => {
      addPreset(nameInput.value.trim() || nextName());
      closeSheet();
    });
    cancelBtn.addEventListener('click', closeSheet);
    nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        confirmBtn.click();
      } else if (event.key === 'Escape') {
        closeSheet();
        saveBtn.focus();
      }
    });
    moreBtn.addEventListener('click', () => openMenu(menu.hidden));
    exportItem.addEventListener('click', exportNow);
    importItem.addEventListener('click', () => {
      openMenu(false);
      say(copy.import_note, 'ok');
    });
    document.addEventListener('pointerdown', (event) => {
      if (!menu.hidden && !menu.contains(event.target) && !moreBtn.contains(event.target)) openMenu(false);
    });

    // ---- snippets: click pastes, real copy copies -----------------------------------
    const pasteSnippet = (snip) => {
      snips.forEach((s) => s.classList.remove('is-on', 'is-selected', 'is-copied'));
      snip.classList.add('is-on', 'is-selected', 'is-copied');
      field.value = snip.dataset.copy;
      commitField();
    };
    snips.forEach((snip) => snip.addEventListener('click', () => pasteSnippet(snip)));

    let hovered = null;
    snips.forEach((snip) => {
      snip.addEventListener('pointerenter', () => (hovered = snip));
      snip.addEventListener('pointerleave', () => {
        if (hovered === snip) hovered = null;
      });
    });
    document.addEventListener('copy', (event) => {
      const focused = snips.find((s) => s === document.activeElement);
      const target = focused || hovered;
      if (!target || !event.clipboardData) return;
      const selection = window.getSelection ? String(window.getSelection()) : '';
      if (selection && !focused) return;
      event.clipboardData.setData('text/plain', target.dataset.copy);
      event.preventDefault();
      takeOver();
      snips.forEach((s) => s.classList.remove('is-on', 'is-selected', 'is-copied'));
      target.classList.add('is-on', 'is-selected', 'is-copied');
      say('');
    });

    // ---- the tour ------------------------------------------------------------
    let token = 0;
    let round = 0;
    const wait = (ms, mine) => new Promise((done) => setTimeout(() => done(mine === token), ms));
    const setStep = (i) => {
      dots.forEach((d, n) => {
        d.classList.toggle('is-current', n === i);
        d.classList.toggle('is-done', n < i);
      });
      caption.textContent = copy[`step_${i + 1}`] || '';
    };
    const caps = (hud, on) => {
      hud.classList.toggle('is-on', on);
      Array.from(hud.children).forEach((c) => c.classList.toggle('is-down', false));
    };
    const pressCaps = async (hud, mine) => {
      caps(hud, true);
      if (!(await wait(140, mine))) return false;
      Array.from(hud.children).forEach((c) => c.classList.add('is-down'));
      if (!(await wait(200, mine))) return false;
      Array.from(hud.children).forEach((c) => c.classList.remove('is-down'));
      return true;
    };
    const press = async (btn, mine, ms = 160) => {
      btn.classList.add('is-down');
      const ok = await wait(ms, mine);
      btn.classList.remove('is-down');
      return ok;
    };

    const reset = () => {
      closeSheet();
      openMenu(false);
      caps(copyHud, false);
      caps(pasteHud, false);
      snips.forEach((s) => s.classList.remove('is-on', 'is-selected', 'is-copied'));
      field.classList.remove('is-focus', 'is-invalid');
      state.mine = [];
      mineTab.hidden = true;
      selectLib('default');
      showFile(false);
      say('');
      show(START, false);
      field.value = format(START);
      markCurrent();
      strip.paint();
    };

    const tour = async (mine) => {
      while (mine === token) {
        const snip = snips[round % snips.length];
        round += 1;
        if (!snip) return;
        reset();

        // 1. copy from the web
        setStep(0);
        if (!(await wait(120, mine))) return;
        snip.classList.add('is-on', 'is-selected');
        if (!(await wait(360, mine))) return;
        if (!(await pressCaps(copyHud, mine))) return;
        snip.classList.add('is-copied');
        if (!(await wait(420, mine))) return;
        caps(copyHud, false);

        // 2. paste under the graph
        setStep(1);
        field.classList.add('is-focus');
        if (!(await wait(240, mine))) return;
        if (!(await pressCaps(pasteHud, mine))) return;
        field.value = snip.dataset.copy;
        if (!(await wait(420, mine))) return;
        caps(pasteHud, false);
        const c = parse(snip.dataset.copy);
        if (c) load(c, { times: 1 });
        say(`cubic-bezier(${format(state.curve)})`, 'ok');
        field.classList.remove('is-focus');
        if (!(await wait(1500, mine))) return;

        // 3. save it with +
        setStep(2);
        if (!(await press(saveBtn, mine))) return;
        openSheet();
        const name = nextName();
        for (let i = 1; i <= name.length; i += 1) {
          nameInput.value = name.slice(0, i);
          if (!(await wait(38, mine))) return;
        }
        if (!(await wait(200, mine))) return;
        if (!(await press(confirmBtn, mine, 140))) return;
        closeSheet();
        addPreset(name);
        if (!(await wait(1100, mine))) return;

        // 4. export the library
        setStep(3);
        if (!(await press(moreBtn, mine, 120))) return;
        openMenu(true);
        if (!(await wait(380, mine))) return;
        exportItem.classList.add('is-hot');
        if (!(await wait(320, mine))) return;
        exportItem.classList.remove('is-hot');
        exportNow();
        if (!(await wait(2000, mine))) return;
        dots.forEach((d) => d.classList.add('is-done'));
        if (!(await wait(700, mine))) return;
      }
    };

    const stopTour = () => {
      token += 1;
    };
    const startTour = () => {
      if (state.yours || still.matches) return;
      token += 1;
      tour(token);
    };

    function takeOver() {
      if (state.yours) return;
      state.yours = true;
      stopTour();
      root.classList.add('is-yours');
      caps(copyHud, false);
      caps(pasteHud, false);
      field.classList.remove('is-focus');
      exportItem.classList.remove('is-hot');
      [saveBtn, moreBtn, confirmBtn].forEach((b) => b.classList.remove('is-down'));
      if (!sheet.hidden && !nameInput.value) nameInput.value = nextName();
      caption.innerHTML = '';
      caption.append(`${copy.turn || ''} `);
      const rest = document.createElement('span');
      rest.textContent = copy.turn_rest || '';
      caption.appendChild(rest);
    }

    stage.addEventListener('pointerdown', takeOver, true);
    stage.addEventListener('keydown', takeOver, true);
    stage.addEventListener('focusin', (event) => {
      if (event.target !== stage) takeOver();
    });

    replay.addEventListener('click', () => {
      state.yours = false;
      root.classList.remove('is-yours');
      round = 0;
      startTour();
    });

    // ---- start ------------------------------------------------------------------
    reset();
    if (typeof ResizeObserver === 'function') new ResizeObserver(measure).observe(svg.parentElement);
    else window.addEventListener('resize', measure);
    measure();

    if (still.matches) {
      // a finished still: the curve pasted, saved and exported, handed over
      const snip = snips[0];
      if (snip) {
        const c = parse(snip.dataset.copy);
        snip.classList.add('is-on', 'is-selected', 'is-copied');
        if (c) load(c, { animate: false });
        addPreset(nextName());
        showFile(true);
      }
      takeOver();
      state.visible = true;
      strip.paint();
      return;
    }

    const update = () => {
      const on = state.visible && !document.hidden;
      if (on) {
        strip.resume();
        if (!state.yours) startTour();
      } else {
        stopTour();
        strip.halt();
      }
    };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          state.visible = entries[entries.length - 1].isIntersecting;
          if (state.visible && !state.yours) round = 0;
          update();
        },
        { threshold: 0.2 }
      ).observe(stage);
    } else {
      state.visible = true;
      update();
    }
    document.addEventListener('visibilitychange', update);
  };

  const boot = (scope) => (scope || document).querySelectorAll('[data-ef-curves]').forEach(setup);
  boot(document);
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
